import {Observable, ReplaySubject} from 'rxjs';
import {environment} from '../../environments/environment';
import { UUID } from '../models/uuid';


export abstract class BaseService {
  // These are PROD urls from Moti
  // httpHost: string = 'http://localhost:8080';
  // wsHost: string = 'ws://localhost:8080/ws/convos';
  httpHost: string = environment.convoUrl;
  wsHost: string = environment.wsUrl + '/ws/convos';


  protected enableHttpLocalhost(enable: boolean = true) {
    if (enable !== true) {
      return;
    }

    if (environment.production) {
      console.error('HTTP localhost set to true in BaseService constructor but ignored for prod');
      return;
    }

    this.httpHost = 'http://localhost:8080';
    this.warnForLocalhost();
  }


  protected enableWsLocalhost(enable: boolean = true) {
    if (enable !== true) {
      return;
    }

    if (environment.production) {
      console.error('WS localhost set to true in BaseService constructor but ignored for prod');
      return;
    }

    this.wsHost = 'ws://localhost:8080/ws/convos';
    this.warnForLocalhost();
  }


  private warnForLocalhost() {
    console.warn(this.consoleLocalhostWarning);
    window.alert(this.dialogLocalhostWarning);
  }


  /**
   * Facilitates caching where many subscribers might all be seeking the same thing at roughly the same time, to
   * avoid hammering the APIs unnecessarily.
   *
   * @param T$Map - A reference to the object where reusable observables are being stored
   * @param id - The key of T$Map where the observable gets stored
   * @param wrapperFunc - The wrapper that supplies the present caller with a Object on which to call
   * @param expireAfterSeconds - When to wipe the value from cache so that the next call results in re-obtaining
   *    the value
   *
   * @private
   */
  protected wrapAndCache<T>(
    T$Map: { [id: UUID]: ReplaySubject<T> },
    id: UUID,
    wrapperFunc: (obs$: ReplaySubject<T>) => void,
    expireAfterSeconds: number = 600,
  ): Observable<T> {
    // If cache exists, return it
    if (T$Map[id]) {
      return T$Map[id];
    }

    // This function is run at expiration time or to facilitate early expiration
    const expire = () => {
      T$Map[id]?.complete();
      delete T$Map[id];
    };

    // This observable will be returned to all callers of the caller of wrapAndCache until expiration
    const obs$ = T$Map[id] = new ReplaySubject(1);

    // Listen for errors in case we need to expire the cache early
    obs$.subscribe({
      error: err => {
        console.error(`wrapAndCache error: ${JSON.stringify(err)}`);
        expire();
      },
    });

    // Run the caller's supplied function
    wrapperFunc(obs$);

    // Automatic expiration (wiping of cache)
    setTimeout(() => {
      expire();
    }, expireAfterSeconds * 1000);

    return obs$;
  }


  private consoleLocalhostWarning = `
**********************************
         Using localhost
**********************************
`;

  private dialogLocalhostWarning = 'Using localhost. Alert engineering@moti immediately if you are seeing this message.';
}
